import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useMemo } from 'react'

import { USER_GROUP_QUERY_KEYS } from '../api/endpoints'
import type { PbxUserItem, UserGroupApi } from '../api/userGroupApi'
import type {
  PaginatedApiResult,
  UserGroupListParams,
} from '../types/api.types'
import type { UserGroup } from '../types/userGroup.types'

export interface UseListUserGroupsOptions {
  api: UserGroupApi
  pbxId: number
  params?: UserGroupListParams
  queryOptions?: Omit<
    UseQueryOptions<PaginatedApiResult<UserGroup> | null, Error>,
    'queryKey' | 'queryFn'
  >
}

export const useListUserGroups = ({
  api,
  pbxId,
  params = {},
  queryOptions,
}: UseListUserGroupsOptions) => {
  const query = useQuery({
    queryKey: USER_GROUP_QUERY_KEYS.list({
      pbxId,
      ...(params as Record<string, unknown>),
    }),
    queryFn: async () => {
      const response = await api.getUserGroups(pbxId, params)
      if (!response.ok) {
        throw new Error('Failed to fetch user groups')
      }
      return response.data ?? null
    },
    ...queryOptions,
  })

  // Message-group "shadow" groups (auto-generated companions of Message
  // Groups) are system-managed and must not be shown or edited here — same as
  // the Fax/Shared VM picklists. Memoize on query.data so the filtered array
  // keeps a stable identity between renders (react-query structural sharing),
  // preserving the downstream useMemos in UserGroupList.
  const { userGroups, removed } = useMemo(() => {
    const rawResults = query.data?.results ?? []
    const filtered = rawResults.filter((group) => !group.is_message_group)
    return {
      userGroups: filtered,
      removed: rawResults.length - filtered.length,
    }
  }, [query.data])

  return {
    ...query,
    userGroups,
    pagination: {
      // Subtract the filtered shadows so the displayed total matches the
      // visible list, and so the truncation guard stays `count > rawResults`
      // (its pre-filter behavior) instead of false-firing on filtered rows.
      count: Math.max(0, (query.data?.count ?? 0) - removed),
      next: query.data?.next,
      previous: query.data?.previous,
    },
  }
}

export interface UseGetUserGroupOptions {
  api: UserGroupApi
  pbxId: number
  id: number
  queryOptions?: Omit<
    UseQueryOptions<UserGroup | null, Error>,
    'queryKey' | 'queryFn'
  >
}

export const useGetUserGroup = ({
  api,
  pbxId,
  id,
  queryOptions,
}: UseGetUserGroupOptions) => {
  const query = useQuery({
    queryKey: USER_GROUP_QUERY_KEYS.detail(id),
    queryFn: async () => {
      const response = await api.getUserGroup(pbxId, id)
      if (!response.ok) {
        throw new Error('Failed to fetch user group')
      }
      return response.data ?? null
    },
    ...queryOptions,
  })

  return {
    ...query,
    userGroup: query.data ?? null,
  }
}

export interface UsePbxUsersOptions {
  api: UserGroupApi
  pbxId: number
  queryOptions?: Omit<
    UseQueryOptions<PbxUserItem[] | null, Error>,
    'queryKey' | 'queryFn'
  >
}

export const usePbxUsers = ({
  api,
  pbxId,
  queryOptions,
}: UsePbxUsersOptions) => {
  const query = useQuery({
    queryKey: ['pbxUsers', pbxId] as const,
    queryFn: async () => {
      const response = await api.getPbxUsers(pbxId)
      if (!response.ok) {
        throw new Error('Failed to fetch PBX users')
      }
      return response.data?.results ?? []
    },
    ...queryOptions,
  })

  return {
    ...query,
    pbxUsers: query.data ?? [],
  }
}
